import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Truck } from 'lucide-react';
import { toast } from 'sonner';

const ReportDamage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    vehicle_id: '',
    position: '',
    description: '',
    severity: 'medium',
    notes: '',
    trip_id: ''
  });

  useEffect(() => {
    loadMyVehicles();
  }, []);

  const loadMyVehicles = async () => {
    try {
      const res = await axios.get('/vehicles');
      // Filter vehicles that are assigned to current user (in_use status)
      const myVehicles = res.data.filter(v => v.status === 'in_use');
      setVehicles(myVehicles);
      
      if (myVehicles.length === 1) {
        // Auto-select if only one vehicle
        setFormData(prev => ({ ...prev, vehicle_id: myVehicles[0].vehicle_id }));
      }
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error('Fehler beim Laden der Fahrzeuge');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.vehicle_id || !formData.position || !formData.description) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/trips/damages', {
        vehicle_id: formData.vehicle_id,
        position: formData.position,
        description: formData.description,
        severity: formData.severity,
        notes: formData.notes,
        trip_id: formData.trip_id || null
      });

      toast.success('Schaden erfolgreich gemeldet - Admin wurde benachrichtigt');
      
      // Reset form
      setFormData({
        vehicle_id: vehicles.length === 1 ? vehicles[0].vehicle_id : '',
        position: '',
        description: '',
        severity: 'medium',
        notes: '',
        trip_id: ''
      });
    } catch (error) {
      console.error('Error reporting damage:', error);
      toast.error('Fehler beim Melden des Schadens');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50';
      case 'medium': return 'bg-orange-500/20 text-orange-300 border-orange-500/50';
      case 'high': return 'bg-red-500/20 text-red-300 border-red-500/50';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/50';
    }
  };

  const selectedVehicle = vehicles.find(v => v.vehicle_id === formData.vehicle_id);

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <Card className="bg-[#0a0a0a] border-gray-800">
        <CardHeader>
          <CardTitle className="text-2xl font-bold flex items-center gap-3">
            <AlertTriangle className="h-7 w-7 text-orange-500" />
            Schaden melden
          </CardTitle>
          <p className="text-gray-400 text-sm mt-2">
            Melden Sie einen Schaden, der während der Fahrt aufgetreten ist. 
            Der Admin wird sofort per E-Mail benachrichtigt.
          </p>
        </CardHeader>
        <CardContent>
          {vehicles.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="h-16 w-16 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400 text-lg font-medium mb-2">
                Kein Fahrzeug in Verwendung
              </p>
              <p className="text-gray-500 text-sm">
                Bitte übernehmen Sie zuerst ein Fahrzeug, um einen Schaden zu melden
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Vehicle Selection */}
              <div>
                <Label className="text-base">Fahrzeug *</Label>
                <Select
                  value={formData.vehicle_id}
                  onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })}
                >
                  <SelectTrigger className="bg-[#1a1a1a] border-gray-700 mt-1.5">
                    <SelectValue placeholder="Fahrzeug wählen..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-gray-700">
                    {vehicles.map(vehicle => (
                      <SelectItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                        {vehicle.license_plate} - {vehicle.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedVehicle && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-400">
                    <span className="font-medium">Ausgewähltes Fahrzeug:</span> {selectedVehicle.license_plate} - {selectedVehicle.model}
                  </p>
                </div>
              )}

              {/* Position */}
              <div>
                <Label className="text-base">Position des Schadens *</Label>
                <Select
                  value={formData.position}
                  onValueChange={(value) => setFormData({ ...formData, position: value })}
                >
                  <SelectTrigger className="bg-[#1a1a1a] border-gray-700 mt-1.5">
                    <SelectValue placeholder="Position wählen..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-gray-700">
                    <SelectItem value="front">Vorne (Stoßstange, Motorhaube)</SelectItem>
                    <SelectItem value="rear">Hinten (Stoßstange, Kofferraum)</SelectItem>
                    <SelectItem value="left">Links (Tür, Seitenspiegel)</SelectItem>
                    <SelectItem value="right">Rechts (Tür, Seitenspiegel)</SelectItem>
                    <SelectItem value="top">Dach</SelectItem>
                    <SelectItem value="interior">Innenraum</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Severity */}
              <div>
                <Label className="text-base mb-2 block">Schweregrad *</Label>
                <div className="grid grid-cols-3 gap-3">
                  {['low', 'medium', 'high'].map((severity) => (
                    <button
                      key={severity}
                      type="button"
                      onClick={() => setFormData({ ...formData, severity })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.severity === severity
                          ? getSeverityColor(severity) + ' border-current'
                          : 'bg-[#1a1a1a] border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}
                    >
                      <p className="font-medium">
                        {severity === 'low' && 'Gering'}
                        {severity === 'medium' && 'Mittel'}
                        {severity === 'high' && 'Hoch'}
                      </p>
                      <p className="text-xs mt-1">
                        {severity === 'low' && 'Kleine Kratzer'}
                        {severity === 'medium' && 'Sichtbare Schäden'}
                        {severity === 'high' && 'Schwere Schäden'}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-base">Detaillierte Beschreibung *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Beschreiben Sie den Schaden so genau wie möglich...&#10;&#10;Beispiel: 'Delle an der Fahrertür, ca. 10cm Durchmesser, beim Ausparken im Supermarkt passiert.'"
                  className="bg-[#1a1a1a] border-gray-700 mt-1.5 min-h-[120px]"
                />
              </div>

              {/* Trip ID (Optional) */}
              <div>
                <Label className="text-base">Fahrt-ID (optional)</Label>
                <Input
                  value={formData.trip_id}
                  onChange={(e) => setFormData({ ...formData, trip_id: e.target.value })}
                  placeholder="Falls vorhanden..."
                  className="bg-[#1a1a1a] border-gray-700 mt-1.5"
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Geben Sie die Fahrt-ID an, falls diese Information verfügbar ist
                </p>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-base">Zusätzliche Notizen (optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Weitere Informationen, Zeugen, Unfallhergang..."
                  className="bg-[#1a1a1a] border-gray-700 mt-1.5 min-h-[80px]"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading || !formData.vehicle_id || !formData.position || !formData.description}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-6 text-base"
              >
                {loading ? (
                  'Wird gemeldet...'
                ) : (
                  <>
                    <AlertTriangle className="h-5 w-5 mr-2" />
                    Schaden jetzt melden
                  </>
                )}
              </Button>

              <div className="p-4 bg-gray-800/30 border border-gray-700 rounded-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-gray-400">
                    <p className="font-medium text-gray-300 mb-1">Was passiert nach der Meldung?</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Der Admin wird sofort per E-Mail benachrichtigt</li>
                      <li>Der Schaden wird im Fahrzeugregister dokumentiert</li>
                      <li>Sie erhalten eine Bestätigung Ihrer Meldung</li>
                    </ul>
                  </div>
                </div>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportDamage;
